import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Car,
  CreditCard,
  Phone,
  FileText,
  Download,
  Calendar,
  MessageSquare,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { driverApplicationAPI } from '../../services/driverApplicationAPI';
import { DriverApplication } from '../../types';

interface AdminDriverApplicationDetailProps {
  applicationId: number;
  onBack: () => void;
  onApplicationUpdated: () => void;
}

const AdminDriverApplicationDetail: React.FC<AdminDriverApplicationDetailProps> = ({
  applicationId,
  onBack,
  onApplicationUpdated
}) => {
  const [application, setApplication] = useState<DriverApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [adminComments, setAdminComments] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    fetchApplication();
  }, [applicationId]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await driverApplicationAPI.getApplication(applicationId);
      setApplication(response.application);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch application');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!reviewAction || !application) return;

    try {
      setReviewing(true);
      const status = reviewAction === 'approve' ? 'approved' : 'rejected';
      await driverApplicationAPI.reviewApplication(application.id, {
        status: status,
        admin_comments: adminComments.trim() || undefined
      });
      
      await fetchApplication();
      onApplicationUpdated();
      setShowReviewModal(false);
      setReviewAction(null);
      setAdminComments('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review application');
    } finally {
      setReviewing(false);
    }
  };

  const handleDownloadDocument = async (documentId: string, filename: string) => {
    try {
      if (!application) return;
      
      const blob = await driverApplicationAPI.downloadDocument(application.id, documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="p-6">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Applications
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">Error</h3>
              <p className="text-red-700">{error || 'Application not found'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Applications
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Driver Application Review</h2>
            <p className="text-gray-600">Application #{application.id}</p>
          </div>
          <div className="flex items-center space-x-3">
            {getStatusIcon(application.application_status)}
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(application.application_status)}`}>
              {application.application_status.charAt(0).toUpperCase() + application.application_status.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {application.application_status === 'pending' && (
        <div className="mb-6 flex space-x-3">
          <button
            onClick={() => {
              setReviewAction('approve');
              setShowReviewModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <CheckCircle className="h-4 w-4" />
            <span>Approve Application</span>
          </button>
          <button
            onClick={() => {
              setReviewAction('reject');
              setShowReviewModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            <XCircle className="h-4 w-4" />
            <span>Reject Application</span>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Applicant Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Applicant Information
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Name:</span>
                <span className="ml-2 font-medium">
                  {[application.user?.first_name, application.user?.last_name].filter(Boolean).join(' ')}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Email:</span>
                <span className="ml-2 font-medium">{application.user?.email}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Full Name:</span>
                <span className="ml-2 font-medium">
                  {application.user?.first_name} {application.user?.last_name}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Application Date:</span>
                <span className="ml-2 font-medium">
                  {new Date(application.submitted_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Driver License Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Driver License Information
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">License Number:</span>
                <span className="ml-2 font-medium">{application.license_number}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Expiry Date:</span>
                <span className="ml-2 font-medium">
                  {new Date(application.license_expiry_date).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Car className="h-5 w-5 mr-2" />
              Vehicle Information
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-sm text-gray-600">Make:</span>
                <span className="ml-2 font-medium">{application.vehicle_make}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Model:</span>
                <span className="ml-2 font-medium">{application.vehicle_model}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Year:</span>
                <span className="ml-2 font-medium">{application.vehicle_year}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Color:</span>
                <span className="ml-2 font-medium">{application.vehicle_color}</span>
              </div>
              <div className="col-span-2">
                <span className="text-sm text-gray-600">Registration:</span>
                <span className="ml-2 font-medium">{application.vehicle_registration}</span>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="h-5 w-5 mr-2" />
              Emergency Contact
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Name:</span>
                <span className="ml-2 font-medium">{application.emergency_contact_name}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Phone:</span>
                <span className="ml-2 font-medium">{application.emergency_contact_phone}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Additional Information */}
          {(application.previous_experience || application.reason_for_driving) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Additional Information
              </h3>
              {application.previous_experience && (
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700">Previous Experience:</span>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded">
                    {application.previous_experience}
                  </p>
                </div>
              )}
              {application.reason_for_driving && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Reason for Driving:</span>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded">
                    {application.reason_for_driving}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Documents */}
          {application.documents && application.documents.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Uploaded Documents
              </h3>
              <div className="space-y-3">
                {application.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-6 w-6 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.original_name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.type.replace('_', ' ')} • {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownloadDocument(doc.id, doc.original_name)}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                      <span className="text-xs">Download</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Review Status */}
          {application.application_status !== 'pending' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                Review Status
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className={`ml-2 px-2 py-1 rounded text-sm font-medium ${getStatusColor(application.application_status)}`}>
                    {application.application_status}
                  </span>
                </div>
                {application.reviewed_at && (
                  <div>
                    <span className="text-sm text-gray-600">Reviewed On:</span>
                    <span className="ml-2 font-medium">
                      {new Date(application.reviewed_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {(application.reviewed_by_first_name || application.reviewed_by_last_name) && (
                  <div>
                    <span className="text-sm text-gray-600">Reviewed By:</span>
                    <span className="ml-2 font-medium">
                      {[application.reviewed_by_first_name, application.reviewed_by_last_name].filter(Boolean).join(' ')}
                    </span>
                  </div>
                )}
                {application.admin_comments && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Admin Comments:</span>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded">
                      {application.admin_comments}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {reviewAction === 'approve' ? 'Approve Application' : 'Reject Application'}
            </h3>
            <p className="text-gray-600 mb-4">
              {reviewAction === 'approve' 
                ? 'Are you sure you want to approve this driver application? The user will be granted driver privileges.'
                : 'Please provide a reason for rejecting this application.'
              }
            </p>
            <div className="mb-4">
              <label htmlFor="adminComments" className="block text-sm font-medium text-gray-700 mb-2">
                Comments {reviewAction === 'reject' && '(Required)'}
              </label>
              <textarea
                id="adminComments"
                value={adminComments}
                onChange={(e) => setAdminComments(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={reviewAction === 'approve' ? 'Optional comments...' : 'Reason for rejection...'}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setReviewAction(null);
                  setAdminComments('');
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReview}
                disabled={reviewing || (reviewAction === 'reject' && !adminComments.trim())}
                className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${
                  reviewAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                }`}
              >
                {reviewing && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>
                  {reviewing 
                    ? 'Processing...' 
                    : reviewAction === 'approve' 
                      ? 'Approve' 
                      : 'Reject'
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDriverApplicationDetail;
